import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import {
  accountShellForOverviewHydrate,
  buildHydratedAccountFromOverviewBundle,
  enrichMyRelationshipAccount,
  mapRelationshipAssessmentAccount,
} from "../../Pages/Relationship/account-relationships/relationshipDataAdapter";
import {
  buildAssessmentCreatePayload,
  buildCoverageBootstrapFromOverview,
  buildOverviewBundle,
  mapAssessmentHistoryRows,
  mapRelationshipLevelRows,
  resolvePairUsersFromOverview,
} from "../../Pages/Relationship/account-relationships/relationshipOverviewTransforms";
import {
  buildCurrentUserStakeholder,
  mergeUserMappingsIntoAccounts,
} from "../../Pages/Relationship/my-relationships/myRelationshipsDataAdapter";
import { resolveInitialStakeholderUserIds } from "../../Pages/Relationship/stakeholderSelectionUtils";
import { get, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import { normalizeUnknownMeddWeight } from "../../Utils/Helper/rlFanCaps";

export interface RelationshipAssessmentStakeholder {
  user_id: string;
  user_name: string;
  title: string | null;
  role: string | null;
  email: string;
  label: string;
  grade: number | null;
  grade_name: string | null;
  join_date: string | null;
}

export interface RelationshipAssessmentStakeholdersResponse {
  account_id: string | null;
  users: RelationshipAssessmentStakeholder[];
}

export interface RelationshipAssessmentAccount {
  accountId: string;
  accountName: string;
  region: string | null;
  city: string | null;
  industryVertical: string | null;
  tier: string | null;
  latestLevel: string | null;
  aggregateRl: number | null;
  aggregateRlMedian: number | null;
  engagementScore: number | null;
  currentMaturityLevel: string | null;
  targetMaturityLevel: string | null;
  maturityProgress: number | null;
  lastMaturityUpdate: string | null;
  logo: string | null;
  relationshipStrength: number | null;
  accountTeamMemberCount: number;
  customerTeamMemberCount: number;
  mappingCount: number;
  rlAssessmentCount: number;
  pendingRl: number;
  totalPossibleMapping: number;
}

export interface RelationshipAssessmentAccountsResponse {
  accounts: RelationshipAssessmentAccount[];
  total: number;
}

export interface RelationshipAccountSummary {
  id: string;
  name: string;
  initials: string;
  industry: string;
  tier: string;
  region: string;
  alLevel: number | null;
  rlAggregate: number | null;
  mappedPairs: number;
  assessedPairs: number;
  pendingAssessments: number;
  coveragePercent: number;
  mappingPossiblePairs: number;
  raw: RelationshipAssessmentAccount;
  currentUserStakeholder?: Record<string, unknown> | null;
  myMappedPairs?: number;
  myAssessedPairs?: number;
  myPendingAssessments?: number;
  sectorColor?: string;
  myPairs?: unknown[];
  contacts?: unknown[];
}

export interface CustomerTeamMemberOverview {
  user_id: string;
  customer_id: string;
  name: string;
  email: string;
  phone?: string | null;
  role?: string | null;
  position?: string | null;
  influence_level?: string | null;
  meddpicc_role?: string | null;
  meddpicc_label?: string | null;
  is_active: boolean;
}

export interface MappedPair {
  id: string;
  account_id: string;
  user_id: string;
  contact_id: string;
  is_active: boolean;
  mapped_by?: string | null;
  mapped_at: string;
  created_at: string;
  updated_at: string;
}

export interface RelationshipAssessment {
  id: string;
  account_id: string;
  user_id: string;
  contact_id: string;
  rl: number;
  posture: string | null;
  rationale: string | null;
  assessed_by?: string | null;
  assessed_at: string;
  created_at: string;
  updated_at: string;
}

export interface RlGoal {
  id: string;
  account_id: string;
  user_id: string;
  contact_id: string;
  target_rl: number;
  target_date: string | null;
  status: string;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountRlAggregate {
  account_id: string;
  weighted: number | null;
  median: number | null;
  pair_count: number;
  medd_labelled_count: number;
  medd_total_count: number;
  computed_at: string | null;
}

export interface RelationshipAccountOverview {
  account_id: string;
  account_team: RelationshipAssessmentStakeholder[];
  sponsors?: RelationshipAssessmentStakeholder[];
  executives?: RelationshipAssessmentStakeholder[];
  remaining?: RelationshipAssessmentStakeholder[];
  customer_team: CustomerTeamMemberOverview[];
  mapped_pairs: MappedPair[];
  assessments: RelationshipAssessment[];
  goals: RlGoal[];
  rl_aggregate: AccountRlAggregate | null;
}

export interface RelationshipAssessmentHistoryEntry {
  id: string;
  rlScore: number | null;
  assessedPosture: string | null;
  rationale: string | null;
  createdAt: string;
  targetRlScore: number | null;
  goalTargetHorizon: number | null;
  status: string | null;
  assessorName: string;
  isCurrentPrimary: boolean;
  reassessBy: string | null;
}

export interface UpsertRelationshipAssessmentArg {
  accountId: string;
  pairId?: string | null;
  payload: RelationshipAssessmentUpsertPayload | Record<string, unknown>;
}

export type RefreshAccountRelationshipMatrixArg = {
  account: Record<string, any>;
  orgSettings?: Record<string, any>;
  force?: boolean;
};

export type FetchAccountCoverageBootstrapArg =
  | string
  | { accountId: string; force?: boolean };

const normalizeCoverageBootstrapArg = (
  arg: FetchAccountCoverageBootstrapArg
) => {
  if (typeof arg === "string") {
    return { accountId: arg, force: false };
  }
  return { accountId: arg.accountId, force: Boolean(arg.force) };
};

export interface RelationshipAssessmentUpsertPayload {
  user_id: string;
  contact_id: string;
  rl: number;
  posture?: string | null;
  rationale?: string | null;
  target_rl?: number | null;
  target_date?: number | string | null;
  status?: string | null;
}

interface RelationshipState {
  assessmentStakeholders: RelationshipAssessmentStakeholdersResponse | null;
  assessmentStakeholdersLoading: boolean;
  selectedStakeholderUserIds: string[];
  viewMyPortfolio: boolean;
  relationshipAccounts: RelationshipAccountSummary[];
  relationshipAccountsLoading: boolean;
  relationshipAccountsTotal: number;
  relationshipAccountsUserIdsKey: string | null;
  myRelationshipAccounts: RelationshipAccountSummary[];
  myRelationshipAccountsLoading: boolean;
  myRelationshipAccountsTotal: number;
  myRelationshipAccountsUserId: string | null;
  myRelationshipWorkspaceUser: Record<string, any> | null;
  myRelationshipMappingsLoading: boolean;
  myRelationshipAccountsStale: boolean;
  upsertAssessmentLoading: boolean;
  coverageBootstrapLoading: boolean;
  matrixRefreshLoading: boolean;
  assessmentHistoryLoading: boolean;
  assessmentLevelsLoading: boolean;
  saveStakeholderRelationshipsLoading: boolean;
  error: string | null;
}

const initialState: RelationshipState = {
  assessmentStakeholders: null,
  assessmentStakeholdersLoading: false,
  selectedStakeholderUserIds: [],
  viewMyPortfolio: false,
  relationshipAccounts: [],
  relationshipAccountsLoading: false,
  relationshipAccountsTotal: 0,
  relationshipAccountsUserIdsKey: null,
  myRelationshipAccounts: [],
  myRelationshipAccountsLoading: false,
  myRelationshipAccountsTotal: 0,
  myRelationshipAccountsUserId: null,
  myRelationshipWorkspaceUser: null,
  myRelationshipMappingsLoading: false,
  myRelationshipAccountsStale: false,
  upsertAssessmentLoading: false,
  coverageBootstrapLoading: false,
  matrixRefreshLoading: false,
  assessmentHistoryLoading: false,
  assessmentLevelsLoading: false,
  saveStakeholderRelationshipsLoading: false,
  error: null,
};

const extractContent = <T>(response: any): T =>
  (response?.content ?? response) as T;

// ---------------------------------------------------------------------------
// Relationship API layer — all HTTP for this feature lives in this slice file.
// ---------------------------------------------------------------------------

const overviewCache = new Map<string, RelationshipAccountOverview>();
const overviewInflight = new Map<
  string,
  Promise<RelationshipAccountOverview>
>();
const matrixHydrateCache = new Map<string, { data: Record<string, any> }>();
const matrixHydrateInflight = new Map<string, Promise<Record<string, any>>>();

const clearRelationshipOverviewCache = (accountId?: string) => {
  if (accountId) {
    overviewCache.delete(String(accountId));
  } else {
    overviewCache.clear();
  }
};

const clearRelationshipMatrixHydrateCache = (accountId?: string) => {
  if (accountId) {
    matrixHydrateCache.delete(String(accountId));
  } else {
    matrixHydrateCache.clear();
  }
};

const clearRelationshipInflightRequests = (accountId?: string) => {
  if (accountId) {
    const key = String(accountId);
    overviewInflight.delete(key);
    matrixHydrateInflight.delete(key);
  } else {
    overviewInflight.clear();
    matrixHydrateInflight.clear();
  }
};

const clearRelationshipAssessmentCaches = () => {
  clearRelationshipOverviewCache();
  clearRelationshipMatrixHydrateCache();
  clearRelationshipInflightRequests();
};

const touchMyRelationshipAccountsStale = (state: RelationshipState) => {
  state.myRelationshipAccountsStale = true;
};

const normalizeAggregateSettings = (settings: Record<string, any> = {}) => ({
  consider_meddpicc_role: settings?.consider_meddpicc_role !== false,
  unknown_meddpicc_default_weight: normalizeUnknownMeddWeight(
    settings?.unknown_meddpicc_default_weight
  ),
});

const shouldForceFullRelationshipHydrate = (account: Record<string, any>) =>
  !account?.pairs?.length || !account?.contacts?.length;

async function fetchOverviewFromApi(
  accountId: string,
  { force = false }: { force?: boolean } = {}
): Promise<RelationshipAccountOverview> {
  const key = String(accountId);
  if (!force && overviewCache.has(key)) {
    return overviewCache.get(key)! as RelationshipAccountOverview;
  }
  if (force) {
    overviewCache.delete(key);
    overviewInflight.delete(key);
  } else if (overviewInflight.has(key)) {
    return overviewInflight.get(key)! as Promise<RelationshipAccountOverview>;
  }

  const promise = (async () => {
    if (force) overviewCache.delete(key);
    const response = await get(
      API_URLS.relationshipAssessmentAccountOverview(accountId)
    );
    const content = extractContent<RelationshipAccountOverview>(response);
    overviewCache.set(key, content);
    return content;
  })().finally(() => {
    overviewInflight.delete(key);
  });

  overviewInflight.set(key, promise);
  return promise;
}

async function fetchOverviewBundleFromApi(
  accountId: string,
  { force = false }: { force?: boolean } = {}
) {
  const overview = await fetchOverviewFromApi(accountId, { force });
  return buildOverviewBundle(overview);
}

export type FetchMyRelationshipAccountsArg =
  | string
  | { userId: string; force?: boolean };

type MyRelationshipAccountsResult = {
  accounts: RelationshipAccountSummary[];
  total: number;
  userId: string;
  workspaceUser: Record<string, any> | null;
};

const normalizeFetchMyRelationshipArg = (
  arg: FetchMyRelationshipAccountsArg
) => {
  if (typeof arg === "string") {
    return { userId: arg, force: false };
  }
  return { userId: arg.userId, force: Boolean(arg.force) };
};

const myRelationshipAccountsInflight = new Map<
  string,
  Promise<MyRelationshipAccountsResult>
>();

type RelationshipAccountsResult = {
  accounts: RelationshipAccountSummary[];
  total: number;
  userIdsKey: string;
};

const relationshipAccountsInflight = new Map<
  string,
  Promise<RelationshipAccountsResult>
>();

export const normalizeRelationshipAccountUserIds = (userIds: string[] = []) =>
  Array.from(
    new Set(userIds.map(id => String(id).trim()).filter(Boolean))
  ).sort();

export const dedupeRelationshipAccounts = (
  accounts: RelationshipAccountSummary[] = []
) => {
  const byId = new Map<string, RelationshipAccountSummary>();
  for (const account of accounts) {
    const id = String(account?.id ?? "").trim();
    if (!id) continue;
    byId.set(id, { ...byId.get(id), ...account, id });
  }
  return Array.from(byId.values()).sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""))
  );
};

export const buildRelationshipAccountsUserIdsKey = (userIds: string[] = []) => {
  const normalized = normalizeRelationshipAccountUserIds(userIds);
  return normalized.length ? normalized.join(",") : "__empty__";
};

/** When every sponsor stakeholder is selected, API expects an empty filter list. */
export const resolveRelationshipAccountsUserIdsForApi = (
  selectedUserIds: string[] = [],
  allStakeholderUserIds: string[] = []
): string[] => {
  const selected = normalizeRelationshipAccountUserIds(selectedUserIds);
  const all = normalizeRelationshipAccountUserIds(allStakeholderUserIds);

  if (
    all.length > 0 &&
    selected.length === all.length &&
    selected.every((id, index) => id === all[index])
  ) {
    return [];
  }

  return selected;
};

export const buildRelationshipAccountsRequestKey = (
  selectedUserIds: string[] = [],
  allStakeholderUserIds: string[] = []
) =>
  buildRelationshipAccountsUserIdsKey(
    resolveRelationshipAccountsUserIdsForApi(
      selectedUserIds,
      allStakeholderUserIds
    )
  );

export const selectAllStakeholderUserIds = (state: {
  relationship: RelationshipState;
}) =>
  (state.relationship.assessmentStakeholders?.users ?? []).map(
    user => user.user_id
  );

async function postRelationshipAssessmentAccounts(
  userIds: string[]
): Promise<RelationshipAccountsResult> {
  const normalizedUserIds = normalizeRelationshipAccountUserIds(userIds);
  const response = await post(API_URLS.relationshipAssessmentAccounts(), {
    user_ids: normalizedUserIds,
  });
  const content =
    extractContent<RelationshipAssessmentAccountsResponse>(response);
  const accounts = dedupeRelationshipAccounts(
    (content.accounts ?? []).map(mapRelationshipAssessmentAccount)
  );
  return {
    accounts,
    total: content.total ?? accounts.length,
    userIdsKey: buildRelationshipAccountsUserIdsKey(normalizedUserIds),
  };
}

async function loadMyRelationshipAccountsForUser(
  userId: string
): Promise<MyRelationshipAccountsResult> {
  const response = await post(API_URLS.relationshipAssessmentAccounts(), {
    user_ids: [userId],
  });
  const content =
    extractContent<RelationshipAssessmentAccountsResponse>(response);
  let accounts = (content.accounts ?? [])
    .map(mapRelationshipAssessmentAccount)
    .map((account, index) => enrichMyRelationshipAccount(account, index))
    .sort((a, b) => a.name.localeCompare(b.name));

  const accountIds = accounts.map(account => account.id);
  let workspaceUser: Record<string, any> | null = null;

  if (accountIds.length > 0) {
    const mappingsResponse = await post(
      API_URLS.relationshipAssessmentUserMappings(),
      {
        user_id: userId,
        account_ids: accountIds,
      }
    );
    const workspace = extractContent<any>(mappingsResponse);
    workspaceUser = workspace?.user ?? null;
    accounts = mergeUserMappingsIntoAccounts(accounts, workspace).map(
      (account, index) => ({
        ...account,
        currentUserStakeholder: buildCurrentUserStakeholder(
          workspaceUser || {}
        ),
        sectorColor:
          account.sectorColor ||
          enrichMyRelationshipAccount(account, index).sectorColor,
      })
    );
  }

  return {
    accounts,
    total: content.total ?? accounts.length,
    userId,
    workspaceUser,
  };
}

/** Fetch account-assigned vendor team for one account (ATM + sponsors + extra sponsors). */
export async function loadAccountTeamMembers(
  accountId: string
): Promise<RelationshipAssessmentStakeholder[]> {
  try {
    const overview = await fetchOverviewFromApi(accountId);
    return overview.account_team ?? [];
  } catch {
    return [];
  }
}

/** Fetch a user's relationship accounts directly without touching Redux state. */
export async function fetchTeamMemberAccountsDirect(userId: string) {
  return loadMyRelationshipAccountsForUser(userId);
}

export const fetchRelationshipAssessmentStakeholders = createAsyncThunk(
  "relationship/fetchRelationshipAssessmentStakeholders",
  async (request: { accountId?: string } | undefined, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.relationshipAssessmentStakeholders(request?.accountId)
      );
      const data =
        extractContent<RelationshipAssessmentStakeholdersResponse>(response);
      const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
      return {
        ...data,
        initialSelectedUserIds: resolveInitialStakeholderUserIds(
          data.users ?? [],
          loggedInUser
        ),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to fetch relationship assessment stakeholders"
      );
    }
  }
);

export const fetchMyRelationshipAccounts = createAsyncThunk(
  "relationship/fetchMyRelationshipAccounts",
  async (arg: FetchMyRelationshipAccountsArg, { rejectWithValue }) => {
    const { userId, force } = normalizeFetchMyRelationshipArg(arg);
    try {
      if (!userId) {
        return rejectWithValue("User id is required for My Relationships");
      }

      if (!force) {
        const inflight = myRelationshipAccountsInflight.get(userId);
        if (inflight) {
          return inflight;
        }
      } else {
        myRelationshipAccountsInflight.delete(userId);
      }

      const task = loadMyRelationshipAccountsForUser(userId);
      myRelationshipAccountsInflight.set(userId, task);

      try {
        return await task;
      } finally {
        if (myRelationshipAccountsInflight.get(userId) === task) {
          myRelationshipAccountsInflight.delete(userId);
        }
      }
    } catch (error: any) {
      myRelationshipAccountsInflight.delete(userId);
      return rejectWithValue(
        error.message || "Failed to fetch my relationship accounts"
      );
    }
  }
);

export const refreshMyRelationshipUserMappings = createAsyncThunk(
  "relationship/refreshMyRelationshipUserMappings",
  async (
    payload: { userId: string; accountIds?: string[] },
    { getState, rejectWithValue }
  ) => {
    try {
      const { userId, accountIds } = payload;
      if (!userId) {
        return rejectWithValue("User id is required");
      }
      const state = getState() as { relationship: RelationshipState };
      const ids =
        accountIds && accountIds.length > 0
          ? accountIds
          : state.relationship.myRelationshipAccounts.map(
              account => account.id
            );
      if (!ids.length) {
        return {
          accounts: state.relationship.myRelationshipAccounts,
          workspaceUser: state.relationship.myRelationshipWorkspaceUser,
        };
      }

      const mappingsResponse = await post(
        API_URLS.relationshipAssessmentUserMappings(),
        {
          user_id: userId,
          account_ids: ids,
        }
      );
      const workspace = extractContent<any>(mappingsResponse);
      const workspaceUser = workspace?.user ?? null;
      const merged = mergeUserMappingsIntoAccounts(
        state.relationship.myRelationshipAccounts,
        workspace
      ).map((account, index) => ({
        ...account,
        currentUserStakeholder: buildCurrentUserStakeholder(
          workspaceUser || {}
        ),
        sectorColor:
          account.sectorColor ||
          enrichMyRelationshipAccount(account, index).sectorColor,
      }));

      return {
        accounts: merged,
        workspaceUser,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to refresh my relationship mappings"
      );
    }
  }
);

export type FetchRelationshipAssessmentAccountsArg =
  | { force?: boolean; userIds?: string[] }
  | undefined;

export const fetchRelationshipAssessmentAccounts = createAsyncThunk(
  "relationship/fetchRelationshipAssessmentAccounts",
  async (
    options: FetchRelationshipAssessmentAccountsArg,
    { getState, rejectWithValue }
  ) => {
    const state = getState() as { relationship: RelationshipState };
    const allStakeholderUserIds = (
      state.relationship.assessmentStakeholders?.users ?? []
    ).map(user => user.user_id);
    const selectedUserIds =
      options?.userIds ?? state.relationship.selectedStakeholderUserIds;
    const userIds = resolveRelationshipAccountsUserIdsForApi(
      selectedUserIds,
      allStakeholderUserIds
    );
    const cacheKey = buildRelationshipAccountsUserIdsKey(userIds);

    try {
      if (options?.force) {
        clearRelationshipAssessmentCaches();
        relationshipAccountsInflight.delete(cacheKey);
      } else {
        const inflight = relationshipAccountsInflight.get(cacheKey);
        if (inflight) {
          return inflight;
        }
      }

      const task = postRelationshipAssessmentAccounts(userIds);
      relationshipAccountsInflight.set(cacheKey, task);

      try {
        return await task;
      } finally {
        if (relationshipAccountsInflight.get(cacheKey) === task) {
          relationshipAccountsInflight.delete(cacheKey);
        }
      }
    } catch (error: any) {
      relationshipAccountsInflight.delete(cacheKey);
      return rejectWithValue(
        error.message || "Failed to fetch relationship assessment accounts"
      );
    }
  },
  {
    condition: (options, { getState }) => {
      if (options?.force) return true;

      const state = (getState() as { relationship: RelationshipState })
        .relationship;
      const allStakeholderUserIds = (
        state.assessmentStakeholders?.users ?? []
      ).map(user => user.user_id);
      const selectedUserIds =
        options?.userIds ?? state.selectedStakeholderUserIds;
      const userIds = resolveRelationshipAccountsUserIdsForApi(
        selectedUserIds,
        allStakeholderUserIds
      );
      const cacheKey = buildRelationshipAccountsUserIdsKey(userIds);

      return state.relationshipAccountsUserIdsKey !== cacheKey;
    },
  }
);

export const fetchAccountCoverageBootstrap = createAsyncThunk(
  "relationship/fetchAccountCoverageBootstrap",
  async (arg: FetchAccountCoverageBootstrapArg, { rejectWithValue }) => {
    try {
      const { accountId, force } = normalizeCoverageBootstrapArg(arg);
      const overview = await fetchOverviewFromApi(accountId, { force });
      return buildCoverageBootstrapFromOverview(overview);
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to load coverage matrix data"
      );
    }
  }
);

export const refreshAccountRelationshipMatrix = createAsyncThunk(
  "relationship/refreshAccountRelationshipMatrix",
  async (
    {
      account,
      orgSettings = {},
      force = false,
    }: RefreshAccountRelationshipMatrixArg,
    { rejectWithValue }
  ) => {
    try {
      const cacheKey = String(account.id);
      const shouldForce = force || shouldForceFullRelationshipHydrate(account);

      if (!shouldForce) {
        const cached = matrixHydrateCache.get(cacheKey);
        if (cached?.data) return cached.data;
        if (matrixHydrateInflight.has(cacheKey)) {
          return matrixHydrateInflight.get(cacheKey) as Promise<
            Record<string, any>
          >;
        }
      } else {
        clearRelationshipMatrixHydrateCache(cacheKey);
        clearRelationshipOverviewCache(cacheKey);
        clearRelationshipInflightRequests(cacheKey);
      }

      const promise = (async () => {
        const bundle = await fetchOverviewBundleFromApi(cacheKey, {
          force: shouldForce,
        });
        const hydrateBase = shouldForce
          ? accountShellForOverviewHydrate(account)
          : account;
        const merged = buildHydratedAccountFromOverviewBundle(
          hydrateBase,
          bundle,
          normalizeAggregateSettings(orgSettings)
        );
        matrixHydrateCache.set(cacheKey, { data: merged });
        return merged;
      })().finally(() => {
        matrixHydrateInflight.delete(cacheKey);
      });

      matrixHydrateInflight.set(cacheKey, promise);
      return promise;
    } catch (error: any) {
      console.warn(
        `relationship overview hydrate failed for ${account?.id}:`,
        error?.message || error
      );
      return account;
    }
  }
);

export const fetchRelationshipAssessmentHistory = createAsyncThunk(
  "relationship/fetchRelationshipAssessmentHistory",
  async (
    {
      accountId,
      userId,
      contactId,
    }: { accountId: string; userId: string; contactId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(
        API_URLS.relationshipAssessmentAccountAssessments(accountId)
      );
      const content = extractContent<{ assessments?: any[] }>(response);
      const all = Array.isArray(content?.assessments)
        ? content.assessments
        : [];
      return mapAssessmentHistoryRows(
        all,
        userId,
        contactId
      ) as RelationshipAssessmentHistoryEntry[];
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to fetch relationship assessment history"
      );
    }
  }
);

export const fetchRelationshipAssessmentLevels = createAsyncThunk(
  "relationship/fetchRelationshipAssessmentLevels",
  async (assessmentLevel: string | null | undefined, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.fetchRelationshipLevels(),
        assessmentLevel
          ? { params: { assessment_level: assessmentLevel } }
          : undefined
      );
      return mapRelationshipLevelRows(extractContent(response));
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to fetch relationship assessment levels"
      );
    }
  }
);

export const saveAccountStakeholderRelationships = createAsyncThunk(
  "relationship/saveAccountStakeholderRelationships",
  async (
    {
      accountId,
      links,
      transferDecisions = [],
      handoffNote,
    }: {
      accountId: string;
      links: Record<string, unknown>[];
      transferDecisions?: Record<string, unknown>[];
      handoffNote?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.getStakeholderRelationships(accountId),
        {
          links,
          transfer_decisions: transferDecisions,
          handoff_note: handoffNote,
        },
        false,
        { timeout: 120000 }
      );
      clearRelationshipOverviewCache(accountId);
      clearRelationshipMatrixHydrateCache(accountId);
      clearRelationshipInflightRequests(accountId);
      return extractContent(response);
    } catch (error: any) {
      const raw = error?.message;
      if (typeof raw === "string" && raw.trim()) {
        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed?.message === "string" && parsed.message.trim()) {
            return rejectWithValue(parsed.message);
          }
        } catch {
          // not JSON — use raw message below
        }
      }
      const data = error?.response?.data;
      const detail = data?.detail;
      const detailMessage =
        typeof detail === "string"
          ? detail
          : typeof detail?.msg === "string"
            ? detail.msg
            : typeof detail?.message === "string"
              ? detail.message
              : null;
      return rejectWithValue(
        data?.error ||
          detailMessage ||
          data?.message ||
          (typeof raw === "string" && raw.trim() ? raw : null) ||
          "Failed to save coverage map"
      );
    }
  }
);

export const upsertRelationshipAssessment = createAsyncThunk(
  "relationship/upsertRelationshipAssessment",
  async (
    { accountId, pairId, payload }: UpsertRelationshipAssessmentArg,
    { rejectWithValue }
  ) => {
    try {
      if (!accountId)
        throw new Error("account_id is required for assessment save");

      let userId = (payload as any).user_id || (payload as any).userId;
      let contactId = (payload as any).contact_id || (payload as any).contactId;

      if ((!userId || !contactId) && pairId) {
        const overview = await fetchOverviewFromApi(accountId);
        const resolved = resolvePairUsersFromOverview(overview, pairId);
        userId = userId || resolved.userId;
        contactId = contactId || resolved.contactId;
      }

      if (!userId || !contactId) {
        throw new Error("Unable to resolve assessment pair");
      }

      const response = await post(
        API_URLS.relationshipAssessmentAccountAssessments(accountId),
        buildAssessmentCreatePayload(userId, contactId, payload)
      );

      clearRelationshipOverviewCache(accountId);
      clearRelationshipMatrixHydrateCache(accountId);
      clearRelationshipInflightRequests(accountId);
      return extractContent(response);
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to save relationship assessment"
      );
    }
  }
);

const RelationshipSlice = createSlice({
  name: "relationship",
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearAssessmentStakeholders: state => {
      state.assessmentStakeholders = null;
      state.selectedStakeholderUserIds = [];
      state.viewMyPortfolio = false;
    },
    setSelectedStakeholderUserIds: (state, action: PayloadAction<string[]>) => {
      state.selectedStakeholderUserIds = normalizeRelationshipAccountUserIds(
        action.payload
      );
      state.viewMyPortfolio = false;
    },
    setViewMyPortfolio: (state, action: PayloadAction<boolean>) => {
      state.viewMyPortfolio = action.payload;
    },
    invalidateRelationshipMatrixCache: (
      _state,
      action: PayloadAction<string | undefined>
    ) => {
      clearRelationshipMatrixHydrateCache(action.payload);
      clearRelationshipOverviewCache(action.payload);
      clearRelationshipInflightRequests(action.payload);
    },
    markMyRelationshipAccountsStale: state => {
      touchMyRelationshipAccountsStale(state);
    },
    clearMyRelationshipAccountsStale: state => {
      state.myRelationshipAccountsStale = false;
    },
    updateRelationshipAccount: (
      state,
      action: PayloadAction<
        Partial<RelationshipAccountSummary> & { id: string }
      >
    ) => {
      const index = state.relationshipAccounts.findIndex(
        account => String(account.id) === String(action.payload.id)
      );
      if (index < 0) return;
      const current = state.relationshipAccounts[index];
      const merged = {
        ...current,
        ...action.payload,
        id: String(action.payload.id ?? current.id),
        name: action.payload.name ?? current.name,
        initials: action.payload.initials ?? current.initials,
        industry: action.payload.industry ?? current.industry,
        tier: action.payload.tier ?? current.tier,
        region: action.payload.region ?? current.region,
        alLevel:
          action.payload.alLevel !== undefined
            ? action.payload.alLevel
            : current.alLevel,
        rlAggregate:
          action.payload.rlAggregate !== undefined
            ? action.payload.rlAggregate
            : current.rlAggregate,
      };
      state.relationshipAccounts[index] = merged;
      state.relationshipAccounts = dedupeRelationshipAccounts(
        state.relationshipAccounts
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchRelationshipAssessmentStakeholders.pending, state => {
        state.assessmentStakeholdersLoading = true;
        state.error = null;
      })
      .addCase(
        fetchRelationshipAssessmentStakeholders.fulfilled,
        (state, action) => {
          state.assessmentStakeholdersLoading = false;
          const { initialSelectedUserIds, ...stakeholders } = action.payload;
          state.assessmentStakeholders = stakeholders;
          if (!state.selectedStakeholderUserIds.length) {
            state.selectedStakeholderUserIds = initialSelectedUserIds ?? [];
          }
          state.error = null;
        }
      )
      .addCase(
        fetchRelationshipAssessmentStakeholders.rejected,
        (state, action) => {
          state.assessmentStakeholdersLoading = false;
          state.assessmentStakeholders = null;
          state.error = action.payload as string;
        }
      )
      .addCase(fetchRelationshipAssessmentAccounts.pending, state => {
        state.relationshipAccountsLoading = true;
        state.error = null;
      })
      .addCase(
        fetchRelationshipAssessmentAccounts.fulfilled,
        (state, action) => {
          state.relationshipAccountsLoading = false;
          state.relationshipAccounts = dedupeRelationshipAccounts(
            action.payload.accounts
          );
          state.relationshipAccountsTotal = action.payload.total;
          state.relationshipAccountsUserIdsKey = action.payload.userIdsKey;
          state.error = null;
        }
      )
      .addCase(
        fetchRelationshipAssessmentAccounts.rejected,
        (state, action) => {
          state.relationshipAccountsLoading = false;
          state.relationshipAccounts = [];
          state.relationshipAccountsTotal = 0;
          state.error = action.payload as string;
        }
      )
      .addCase(fetchMyRelationshipAccounts.pending, state => {
        state.myRelationshipAccountsLoading = true;
        state.error = null;
      })
      .addCase(fetchMyRelationshipAccounts.fulfilled, (state, action) => {
        state.myRelationshipAccountsLoading = false;
        state.myRelationshipAccounts = action.payload.accounts;
        state.myRelationshipAccountsTotal = action.payload.total;
        state.myRelationshipAccountsUserId = action.payload.userId;
        state.myRelationshipWorkspaceUser =
          action.payload.workspaceUser ?? null;
        state.myRelationshipAccountsStale = false;
        state.error = null;
      })
      .addCase(fetchMyRelationshipAccounts.rejected, (state, action) => {
        state.myRelationshipAccountsLoading = false;
        state.myRelationshipAccounts = [];
        state.myRelationshipAccountsTotal = 0;
        state.myRelationshipAccountsUserId = null;
        state.myRelationshipWorkspaceUser = null;
        state.error = action.payload as string;
      })
      .addCase(refreshMyRelationshipUserMappings.pending, state => {
        state.myRelationshipMappingsLoading = true;
        state.error = null;
      })
      .addCase(refreshMyRelationshipUserMappings.fulfilled, (state, action) => {
        state.myRelationshipMappingsLoading = false;
        state.myRelationshipAccounts = action.payload.accounts;
        state.myRelationshipWorkspaceUser =
          action.payload.workspaceUser ?? state.myRelationshipWorkspaceUser;
        state.myRelationshipAccountsStale = false;
        state.error = null;
      })
      .addCase(refreshMyRelationshipUserMappings.rejected, (state, action) => {
        state.myRelationshipMappingsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAccountCoverageBootstrap.pending, state => {
        state.coverageBootstrapLoading = true;
      })
      .addCase(fetchAccountCoverageBootstrap.fulfilled, state => {
        state.coverageBootstrapLoading = false;
      })
      .addCase(fetchAccountCoverageBootstrap.rejected, state => {
        state.coverageBootstrapLoading = false;
      })
      .addCase(refreshAccountRelationshipMatrix.pending, state => {
        state.matrixRefreshLoading = true;
      })
      .addCase(refreshAccountRelationshipMatrix.fulfilled, state => {
        state.matrixRefreshLoading = false;
      })
      .addCase(refreshAccountRelationshipMatrix.rejected, state => {
        state.matrixRefreshLoading = false;
      })
      .addCase(fetchRelationshipAssessmentHistory.pending, state => {
        state.assessmentHistoryLoading = true;
      })
      .addCase(fetchRelationshipAssessmentHistory.fulfilled, state => {
        state.assessmentHistoryLoading = false;
      })
      .addCase(fetchRelationshipAssessmentHistory.rejected, state => {
        state.assessmentHistoryLoading = false;
      })
      .addCase(fetchRelationshipAssessmentLevels.pending, state => {
        state.assessmentLevelsLoading = true;
      })
      .addCase(fetchRelationshipAssessmentLevels.fulfilled, state => {
        state.assessmentLevelsLoading = false;
      })
      .addCase(fetchRelationshipAssessmentLevels.rejected, state => {
        state.assessmentLevelsLoading = false;
      })
      .addCase(saveAccountStakeholderRelationships.pending, state => {
        state.saveStakeholderRelationshipsLoading = true;
      })
      .addCase(saveAccountStakeholderRelationships.fulfilled, state => {
        state.saveStakeholderRelationshipsLoading = false;
        touchMyRelationshipAccountsStale(state);
      })
      .addCase(saveAccountStakeholderRelationships.rejected, state => {
        state.saveStakeholderRelationshipsLoading = false;
      })
      .addCase(upsertRelationshipAssessment.pending, state => {
        state.upsertAssessmentLoading = true;
        state.error = null;
      })
      .addCase(upsertRelationshipAssessment.fulfilled, state => {
        state.upsertAssessmentLoading = false;
        touchMyRelationshipAccountsStale(state);
      })
      .addCase(upsertRelationshipAssessment.rejected, (state, action) => {
        state.upsertAssessmentLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearAssessmentStakeholders,
  invalidateRelationshipMatrixCache,
  markMyRelationshipAccountsStale,
  clearMyRelationshipAccountsStale,
  updateRelationshipAccount,
  setSelectedStakeholderUserIds,
  setViewMyPortfolio,
} = RelationshipSlice.actions;

export default RelationshipSlice.reducer;
